/**
 * ActionIcons — real SVG icons for every basketball action.
 * Replaces the emoji strings baked into sport configs. Emojis look dated
 * and inconsistent across platforms; these are stroke-based line icons
 * that scale cleanly and pick up the wheel's accent color.
 */

import React, { useEffect, useState } from 'react';
import { getCustomIconsCached, sanitizeSvg, getCacheVersion } from '../../utils/customIcons';

const stroke = 'currentColor';

const wrap = (paths, viewBox = '0 0 24 24') => ({ size = 24 }) => (
  <svg width={size} height={size} viewBox={viewBox} fill="none"
    stroke={stroke} strokeWidth="1.6"
    strokeLinecap="round" strokeLinejoin="round"
    aria-hidden="true">
    {paths}
  </svg>
);

const Move = wrap(
  <>
    <path d="M12 3v18M3 12h18" />
    <path d="M8 7l-4 5 4 5M16 7l4 5-4 5M7 8l5-4 5 4M7 16l5 4 5-4" />
  </>
);

const Cut = wrap(
  <>
    <path d="M4 20l7-7" />
    <path d="M11 13l4-4" />
    <path d="M15 9l5-5" strokeDasharray="2 2" />
    <path d="M18 8l2-4-4 2" />
  </>
);

const Dribble = wrap(
  <>
    <circle cx="12" cy="10" r="4.5" />
    <path d="M7.6 10a4.5 4.5 0 0 1 8.8 0M12 5.5a4.5 4.5 0 0 1 0 9M7.6 10c1-1.6 2.4-3.6 4.4-4.5M12 14.5c1.2-1.4 2.4-3.4 4.4-4.5" />
    <path d="M8 17v2M12 18v2.5M16 17v2" />
    <path d="M6 22h12" strokeDasharray="2 2" opacity="0.6" />
  </>
);

const Pass = wrap(
  <>
    <path d="M4 12h14" />
    <path d="M14 6l6 6-6 6" />
  </>
);

const BouncePass = wrap(
  <>
    <path d="M4 8c3 0 5 6 10 6s5-6 8-6" />
    <path d="M17 11l4 3-4 3" />
    <circle cx="9" cy="14" r="1.2" fill={stroke} stroke="none" />
  </>
);

const LobPass = wrap(
  <>
    <path d="M4 18c2-9 12-9 15-2" />
    <path d="M15 12l4-2 0 4" />
  </>
);

const SkipPass = wrap(
  <>
    <path d="M4 18l14-12" strokeDasharray="3 2" />
    <path d="M12 6h6v6" />
  </>
);

const EntryPass = wrap(
  <>
    <path d="M3 12h11" />
    <path d="M10 6l4 6-4 6" />
    <rect x="16" y="6" width="5" height="12" rx="1" />
  </>
);

const Shot = wrap(
  <>
    <path d="M4 20c3-14 13-14 16 0" />
    <circle cx="12" cy="4" r="2" />
    <path d="M8 20h8" strokeWidth="2.2" />
  </>
);

const Screen = wrap(
  <>
    <rect x="4" y="10" width="16" height="4" rx="1" />
    <path d="M7 6l-2 4M17 6l2 4M7 18l-2-4M17 18l2-4" opacity="0.4" />
  </>
);

const Rebound = wrap(
  <>
    <path d="M12 4v6" />
    <path d="M9 7l3-3 3 3" />
    <circle cx="12" cy="15" r="4" />
    <path d="M4 20c3-2 5-2 8-2s5 0 8 2" />
  </>
);

const Fallback = wrap(
  <>
    <circle cx="12" cy="12" r="8" />
    <path d="M12 8v4l3 2" />
  </>
);

const ICONS = {
  move: Move,
  cut: Cut,
  dribble: Dribble,
  pass: Pass,
  'bounce-pass': BouncePass,
  'lob-pass': LobPass,
  'skip-pass': SkipPass,
  'entry-pass': EntryPass,
  shot: Shot,
  'jump-shot': Shot,
  layup: Shot,
  screen: Screen,
  'set-screen': Screen,
  'ball-screen': Screen,
  'off-ball-screen': Screen,
  rebound: Rebound,
};

/**
 * @param {{ actionId: string, size?: number, sportId?: string, emojiFallback?: string }} props
 *
 * Resolution order:
 *   1. Custom SVG from IDB (getCustomIconsCached) — user-authored override
 *   2. Bundled SVG in ICONS[]
 *   3. emojiFallback string (renders as text) if provided
 *   4. Fallback SVG (question-mark style)
 */
export const ActionIcon = ({ actionId, size = 24, sportId = 'basketball', emojiFallback }) => {
  const [customSvg, setCustomSvg] = useState(null);
  const [ver, setVer] = useState(0);

  useEffect(() => {
    let alive = true;
    getCustomIconsCached().then((map) => {
      if (!alive) return;
      const key = `${sportId}:${actionId}`;
      setCustomSvg(map[key] || null);
    });
    return () => { alive = false; };
    // Re-fetch when the cache version bumps (setCustomIcon / deleteCustomIcon).
  }, [actionId, sportId, ver]);

  useEffect(() => {
    const id = setInterval(() => {
      const cur = getCacheVersion();
      if (cur !== ver) setVer(cur);
    }, 400);
    return () => clearInterval(id);
  }, [ver]);

  if (customSvg) {
    const safe = sanitizeSvg(customSvg);
    return (
      <span
        style={{ display: 'inline-flex', width: size, height: size, color: 'currentColor', alignItems: 'center', justifyContent: 'center' }}
        // Content already sanitized twice (on save + on render). Wrapper span
        // keeps sizing predictable regardless of the SVG's own width/height.
        dangerouslySetInnerHTML={{ __html: safe }}
      />
    );
  }
  const Cmp = ICONS[actionId];
  if (Cmp) return <Cmp size={size} />;
  if (emojiFallback) {
    return <span style={{ fontSize: Math.round(size * 0.85), lineHeight: 1 }}>{emojiFallback}</span>;
  }
  return <Fallback size={size} />;
};

// Legacy predicate — kept true so ActionWheelV2 always routes through
// <ActionIcon /> (which now handles custom + emoji fallback internally).
export const hasSvgIcon = () => true;

// Metadata export so the Icons page can enumerate every bundled-icon slot.
export const BUNDLED_ICON_IDS = Object.keys(ICONS);
